import { BACKEND_URL } from '../config';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Leaf, Upload, LogOut, History, CreditCard, Languages, Star, User, Settings, HelpCircle, ChevronDown, Cloud, CloudRain, Sun, CloudDrizzle, AlertTriangle, TrendingUp, MessageCircle, Home, Lock, Wrench, Database, FlaskConical } from 'lucide-react';

// Feature flag - set to true when disease detection is ready
const DISEASE_DETECTION_ENABLED = false;

const Dashboard = ({ user, token, language, onLogout, onToggleLanguage, onUserUpdate }) => {
  const navigate = useNavigate();
  const [selectedFiles, setSelectedFiles] = useState([null, null, null]); // Changed to array for 3 images
  const [previewUrls, setPreviewUrls] = useState([null, null, null]); // Changed to array
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');
  const [showUserMenu, setShowUserMenu] = useState(false);
  
  // Interactive diagnosis states
  const [diagnosticMode, setDiagnosticMode] = useState('upload'); // 'upload', 'descriptions', 'questions', 'result'
  const [sessionId, setSessionId] = useState(null);
  const [imageDescriptions, setImageDescriptions] = useState([]); // New: store AI descriptions of images
  const [initialObservation, setInitialObservation] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [conversationHistory, setConversationHistory] = useState([]);
  
  // Weather states
  const [weatherData, setWeatherData] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(false);
  
  // Helper function to get weather icon
  const getWeatherIcon = (weathercode) => {
    // WMO Weather codes
    if (weathercode === 0) return <Sun size={24} />;
    if (weathercode <= 3) return <Cloud size={24} />;
    if (weathercode >= 51 && weathercode <= 67) return <CloudDrizzle size={24} />;
    if (weathercode >= 80 && weathercode <= 99) return <CloudRain size={24} />;
    return <Sun size={24} />;
  };
  
  const getWeatherDescription = (weathercode) => {
    if (weathercode === 0) return 'Clear';
    if (weathercode <= 3) return 'Cloudy';
    if (weathercode >= 51 && weathercode <= 67) return 'Rain';
    if (weathercode >= 80 && weathercode <= 99) return 'Heavy Rain';
    return 'Clear';
  };

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching stats:', err);
    }
  }, [token]);

  const fetchWeather = useCallback(async () => {
    setWeatherLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/weather`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setWeatherData(data);
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching weather:', err);
    } finally {
      setWeatherLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchStats();
    fetchWeather();
  }, [fetchStats, fetchWeather]);

  const handleFileChange = (e, index) => {
    const file = e.target.files[0];
    if (file) {
      const newFiles = [...selectedFiles];
      const newPreviews = [...previewUrls];
      
      newFiles[index] = file;
      newPreviews[index] = URL.createObjectURL(file);
      
      setSelectedFiles(newFiles);
      setPreviewUrls(newPreviews);
      setResult(null);
      setError('');
    }
  };

  const handleStartDiagnosis = async () => {
    // Check if all 3 images are uploaded
    if (!selectedFiles[0] || !selectedFiles[1] || !selectedFiles[2]) {
      setError(language === 'hi' ? 'कृपया तीनों चित्र अपलोड करें' : 'Please upload all 3 images from different angles');
      return;
    }

    setScanning(true);
    setError('');
    setDiagnosticMode('descriptions'); // First show descriptions

    try {
      const formData = new FormData();
      // Append all 3 images
      formData.append('file1', selectedFiles[0]);
      formData.append('file2', selectedFiles[1]);
      formData.append('file3', selectedFiles[2]);

      const response = await fetch(`${BACKEND_URL}/api/scan/start-multi`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (response.ok) {
        setSessionId(data.session_id);
        setImageDescriptions(data.image_descriptions || []); // Store AI descriptions
        setInitialObservation(data.initial_observation);
        setQuestions(data.questions || []);
        setCurrentQuestionIndex(0);
        setAnswers([]);
        setConversationHistory([]);
      } else {
        setError(data.detail || 'Failed to start diagnosis');
        setDiagnosticMode('upload');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      setDiagnosticMode('upload');
    } finally {
      setScanning(false);
    }
  };

  const handleAnswerQuestion = async (answer) => {
    const currentQuestion = questions[currentQuestionIndex];
    const newAnswer = {
      question: language === 'en' ? currentQuestion.question_en : currentQuestion.question_hi,
      answer: answer
    };

    const updatedHistory = [...conversationHistory, newAnswer];
    setConversationHistory(updatedHistory);
    setAnswers([...answers, answer]);

    // Check if more questions
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      // All questions answered, get diagnosis
      await getFinalDiagnosis(updatedHistory);
    }
  };

  const getFinalDiagnosis = async (history) => {
    setScanning(true);
    setError('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/scan/answer/${sessionId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          answer: '',
          conversation_history: history
        })
      });

      const data = await response.json();

      if (response.ok) {
        if (data.is_final) {
          // Final diagnosis received
          setResult(data.diagnosis);
          setDiagnosticMode('result');
          fetchStats();
          onUserUpdate();
        } else {
          // More questions
          setQuestions(data.questions || []);
          setCurrentQuestionIndex(0);
        }
      } else {
        setError(data.detail || 'Failed to get diagnosis');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setScanning(false);
    }
  };

  const handleRestart = () => {
    setDiagnosticMode('upload');
    setSessionId(null);
    setInitialObservation(null);
    setQuestions([]);
    setCurrentQuestionIndex(0);
    setAnswers([]);
    setConversationHistory([]);
    setResult(null);
    setSelectedFiles([null, null, null]);
    setPreviewUrls([null, null, null]);
    setImageDescriptions([]);
  };

  const translations = {
    en: {
      title: 'Crop Disease Scanner',
      uploadImage: 'Upload Crop Image',
      scanNow: 'Scan Now',
      scanning: 'Analyzing...',
      results: 'Scan Results',
      disease: 'Disease Detected',
      description: 'Description',
      treatment: 'Treatment',
      prevention: 'Prevention',
      severity: 'Severity',
      stats: 'Your Stats',
      totalScans: 'Total Scans',
      scansToday: 'Scans Today',
      remaining: 'Remaining Today',
      currentPlan: 'Current Plan',
      upgradePlan: 'Upgrade to Pro',
      viewHistory: 'View History',
      logout: 'Logout'
    },
    hi: {
      title: 'फसल रोग स्कैनर',
      uploadImage: 'फसल की छवि अपलोड करें',
      scanNow: 'अभी स्कैन करें',
      scanning: 'विश्लेषण हो रहा है...',
      results: 'स्कैन परिणाम',
      disease: 'पहचाना गया रोग',
      description: 'विवरण',
      treatment: 'उपचार',
      prevention: 'रोकथाम',
      severity: 'गंभीरता',
      stats: 'आपके आंकड़े',
      totalScans: 'कुल स्कैन',
      scansToday: 'आज के स्कैन',
      remaining: 'आज शेष',
      currentPlan: 'वर्तमान योजना',
      upgradePlan: 'प्रो में अपग्रेड करें',
      viewHistory: 'इतिहास देखें',
      logout: 'लॉगआउट'
    }
  };

  const t = translations[language];

  return (
    <div className="dashboard-page">
      {/* Header */}
      <header className="app-header">
        <div className="header-content">
          <div className="logo" onClick={() => navigate('/')} style={{cursor: 'pointer'}}>
            <Leaf size={32} />
            <span>BoAI</span>
          </div>
          <div className="header-actions">
            <button className="icon-btn home-btn" onClick={() => navigate('/')} title={language === 'hi' ? 'होम' : 'Home'}>
              <Home size={20} />
            </button>
            <div className="tier-badge" data-testid="tier-badge">
              {user?.tier || 'free'}
            </div>
            <button className="icon-btn" onClick={() => navigate('/forum')} data-testid="forum-btn" title="Forum">
              <MessageCircle size={20} />
            </button>
            <button className="icon-btn" onClick={() => navigate('/farm-insights')} data-testid="farm-insights-btn" title="Farm Insights">
              <TrendingUp size={20} />
            </button>
            {user?.tier === 'paid' && (
              <button className="icon-btn premium-btn" onClick={() => navigate('/premium')} data-testid="premium-btn">
                <Star size={20} />
              </button>
            )}
            <button className="lang-toggle" onClick={onToggleLanguage} data-testid="lang-toggle-btn">
              <Languages size={16} /> {language.toUpperCase()}
            </button>
            <button className="icon-btn" onClick={() => navigate('/history')} data-testid="history-btn">
              <History size={20} />
            </button>
            
            {/* User Profile Dropdown */}
            <div className="user-menu-container">
              <button 
                className="user-menu-btn" 
                onClick={() => setShowUserMenu(!showUserMenu)}
                data-testid="user-menu-btn"
              >
                <User size={20} />
                <ChevronDown size={16} />
              </button>
              
              {showUserMenu && (
                <div className="user-dropdown" data-testid="user-dropdown">
                  <div className="user-dropdown-header">
                    <User size={24} />
                    <div>
                      <p className="user-email">{user?.email}</p>
                      <p className="user-tier">{user?.tier || 'free'} plan</p>
                    </div>
                  </div>
                  <div className="user-dropdown-divider"></div>
                  <button 
                    className="user-dropdown-item" 
                    onClick={() => { setShowUserMenu(false); navigate('/account'); }}
                    data-testid="account-info-btn"
                  >
                    <User size={18} />
                    Account Info
                  </button>
                  <button 
                    className="user-dropdown-item" 
                    onClick={() => { setShowUserMenu(false); navigate('/settings'); }}
                    data-testid="settings-btn"
                  >
                    <Settings size={18} />
                    Account Settings
                  </button>
                  <button 
                    className="user-dropdown-item" 
                    onClick={() => { setShowUserMenu(false); navigate('/help'); }}
                    data-testid="help-btn"
                  >
                    <HelpCircle size={18} />
                    Help
                  </button>
                  <div className="user-dropdown-divider"></div>
                  <button 
                    className="user-dropdown-item logout" 
                    onClick={() => { setShowUserMenu(false); onLogout(); }}
                    data-testid="logout-btn"
                  >
                    <LogOut size={18} />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="dashboard-content">
        <div className="container">
          {/* Stats Section */}
          {stats && (
            <div className="stats-grid">
              <div className="stat-card">
                <h3>{stats.total_scans}</h3>
                <p>{t.totalScans}</p>
              </div>
              <div className="stat-card">
                <h3>{stats.scans_today}</h3>
                <p>{t.scansToday}</p>
              </div>
              <div className="stat-card">
                <h3>{stats.scans_remaining === 'unlimited' ? '∞' : stats.scans_remaining}</h3>
                <p>{t.remaining}</p>
              </div>
              <div className="stat-card">
                <h3>{stats.tier.toUpperCase()}</h3>
                <p>{t.currentPlan}</p>
              </div>
            </div>
          )}

          {/* Weather & Pest Alert Section */}
          {weatherData && (
            <div className="weather-section">
              <h2 className="section-title">
                <Cloud size={24} />
                {language === 'en' ? 'Weather & Pest Alerts' : 'मौसम और कीट अलर्ट'}
              </h2>
              
              {/* Pest Alert Banner */}
              <div className={`pest-alert ${weatherData.pest_alerts.risk_level.toLowerCase()}`}>
                <AlertTriangle size={24} />
                <div>
                  <h3>{weatherData.pest_alerts.risk_level} Risk / {weatherData.pest_alerts.risk_level === 'High' ? 'उच्च' : weatherData.pest_alerts.risk_level === 'Medium' ? 'मध्यम' : 'कम'} जोखिम</h3>
                  <p>{language === 'en' ? weatherData.pest_alerts.alerts_en : weatherData.pest_alerts.alerts_hi}</p>
                </div>
              </div>
              
              {/* 7-Day Forecast */}
              <div className="forecast-grid">
                {weatherData.forecast_7day.map((day, index) => (
                  <div key={index} className="forecast-card">
                    {getWeatherIcon(day.weathercode)}
                    <h4>{day.day}</h4>
                    <p className="temp">{day.temp_max}°C / {day.temp_min}°C</p>
                    <p className="rain">💧 {day.rain_probability}%</p>
                    <p className="condition">{getWeatherDescription(day.weathercode)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {weatherLoading && (
            <div className="weather-loading">
              <Cloud size={32} className="spin" />
              <p>{language === 'en' ? 'Loading weather data...' : 'मौसम डेटा लोड हो रहा है...'}</p>
            </div>
          )}

          {/* Upgrade Banner */}
          {user?.tier === 'free' && (
            <div className="upgrade-banner" data-testid="upgrade-banner">
              <div className="upgrade-content">
                <h3>Unlock Premium Features</h3>
                <p>Soil Analysis, Weather Forecast, AI Chatbot & Expert Advice</p>
                <p>मिट्टी विश्लेषण, मौसम पूर्वानुमान, AI चैटबॉट और विशेषज्ञ सलाह</p>
              </div>
              <button 
                className="btn-primary" 
                onClick={() => navigate('/payment')}
                data-testid="upgrade-btn"
              >
                <CreditCard size={18} /> {t.upgradePlan}
              </button>
            </div>
          )}

          {/* Subscription Renewal Reminder */}
          {user?.tier === 'paid' && stats?.days_remaining !== null && stats?.days_remaining <= 7 && (
            <div className="renewal-banner" data-testid="renewal-banner">
              <div className="renewal-content">
                <h3>⚠️ Subscription Expiring Soon</h3>
                <p>Your premium subscription expires in {stats.days_remaining} days</p>
                <p>आपकी प्रीमियम सदस्यता {stats.days_remaining} दिनों में समाप्त हो जाएगी</p>
              </div>
              <button 
                className="btn-primary" 
                onClick={() => navigate('/payment')}
              >
                Renew Now / अभी नवीनीकरण करें
              </button>
            </div>
          )}

          {/* Scanner Section */}
          <div className="scanner-section">
            <h2 className="section-title" data-testid="scanner-title">{t.title}</h2>
            
            {/* Feature Locked - Under Development */}
            {!DISEASE_DETECTION_ENABLED ? (
              <div className="feature-locked-container">
                <div className="locked-card">
                  <div className="locked-header">
                    <div className="locked-icon-wrapper">
                      <Lock size={48} className="lock-icon" />
                      <div className="pulse-ring"></div>
                    </div>
                    <div className="under-dev-badge">
                      <Wrench size={16} />
                      <span>{language === 'hi' ? 'निर्माणाधीन' : 'UNDER DEVELOPMENT'}</span>
                    </div>
                  </div>

                  <div className="locked-content">
                    <h3>
                      {language === 'hi' 
                        ? '🔬 फसल रोग पहचान प्रणाली' 
                        : '🔬 Crop Disease Detection System'}
                    </h3>
                    <h4>
                      {language === 'hi'
                        ? 'हम कुछ खास बना रहे हैं!'
                        : "We're Building Something Special!"}
                    </h4>

                    <p className="locked-description">
                      {language === 'hi'
                        ? 'हम अपने AI मॉडल को आपके क्षेत्र की फसलों के लिए प्रशिक्षित कर रहे हैं। जब यह तैयार होगा, तो आप 90%+ सटीकता के साथ रोग पहचान कर पाएंगे।'
                        : 'We are training our AI model with region-specific crop disease data. When ready, you will get 90%+ accurate disease detection.'}
                    </p>

                    <div className="development-progress">
                      <div className="progress-header">
                        <FlaskConical size={20} />
                        <span>{language === 'hi' ? 'विकास प्रगति' : 'Development Progress'}</span>
                      </div>
                      <div className="progress-items">
                        <div className="progress-item completed">
                          <span className="check">✓</span>
                          <span>{language === 'hi' ? 'AI मॉडल आर्किटेक्चर' : 'AI Model Architecture'}</span>
                        </div>
                        <div className="progress-item completed">
                          <span className="check">✓</span>
                          <span>{language === 'hi' ? 'मल्टी-इमेज अपलोड' : 'Multi-Image Upload'}</span>
                        </div>
                        <div className="progress-item in-progress">
                          <span className="loading-dot"></span>
                          <span>{language === 'hi' ? 'कस्टम डेटाबेस प्रशिक्षण' : 'Custom Database Training'}</span>
                        </div>
                        <div className="progress-item pending">
                          <span className="circle">○</span>
                          <span>{language === 'hi' ? 'सटीकता परीक्षण' : 'Accuracy Testing'}</span>
                        </div>
                        <div className="progress-item pending">
                          <span className="circle">○</span>
                          <span>{language === 'hi' ? 'सार्वजनिक रिलीज़' : 'Public Release'}</span>
                        </div>
                      </div>
                    </div>

                    <div className="locked-stats">
                      <div className="locked-stat">
                        <Database size={24} />
                        <div>
                          <span className="stat-number">100+</span>
                          <span className="stat-label">{language === 'hi' ? 'रोग डेटाबेस' : 'Diseases'}</span>
                        </div>
                      </div>
                      <div className="locked-stat">
                        <TrendingUp size={24} />
                        <div>
                          <span className="stat-number">90%+</span>
                          <span className="stat-label">{language === 'hi' ? 'लक्ष्य सटीकता' : 'Target Accuracy'}</span>
                        </div>
                      </div>
                    </div>

                    <div className="notify-box">
                      <p>
                        {language === 'hi'
                          ? '📢 जब यह फीचर लाइव होगा, आपको सूचित किया जाएगा!'
                          : '📢 You will be notified when this feature goes live!'}
                      </p>
                    </div>

                    <div className="meanwhile-section">
                      <h5>{language === 'hi' ? 'इस बीच, इन सुविधाओं का उपयोग करें:' : 'Meanwhile, explore these features:'}</h5>
                      <div className="feature-buttons">
                        <button className="feature-btn" onClick={() => navigate('/farm-insights')}>
                          🌾 {language === 'hi' ? 'कृषि सेवाएं' : 'Farm Insights'}
                        </button>
                        <button className="feature-btn" onClick={() => navigate('/forum')}>
                          💬 {language === 'hi' ? 'समुदाय फोरम' : 'Community Forum'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
            {/* Beta Version Notice */}
            <div className="beta-notice">
              <div className="beta-icon">🔬</div>
              <div className="beta-content">
                <h3>
                  <span className="beta-badge">BETA VERSION</span>
                  {language === 'hi' ? 'बीटा संस्करण में' : 'Currently in Beta'}
                </h3>
                <p>
                  {language === 'hi' 
                    ? 'यह बीटा संस्करण वर्तमान में लगभग 50% सटीकता के साथ काम कर रहा है। हम अंतिम संस्करण को 90% सटीकता के साथ तैनात करने की दिशा में सक्रिय रूप से काम कर रहे हैं।' 
                    : 'Our Beta version is currently operating at approximately 50% accuracy. We are actively working to deploy the final version with 90% accuracy.'}
                </p>
                <div className="beta-progress">
                  <div className="progress-bar">
                    <div className="progress-fill" style={{width: '50%'}}>
                      <span className="progress-label">50% Accuracy</span>
                    </div>
                  </div>
                  <div className="progress-target">
                    <span className="target-icon">🎯</span>
                    <span className="target-text">
                      {language === 'hi' ? 'लक्ष्य: 90% सटीकता' : 'Target: 90% Accuracy'}
                    </span>
                  </div>
                </div>
                <p className="beta-thanks">
                  {language === 'hi' 
                    ? '🙏 बीटा परीक्षण में भाग लेने के लिए धन्यवाद! आपकी प्रतिक्रिया हमें बेहतर बनाने में मदद करती है।' 
                    : '🙏 Thank you for participating in our beta testing! Your feedback helps us improve.'}
                </p>
              </div>
            </div>
            
            {/* Upload Mode - 3 Images */}
            {diagnosticMode === 'upload' && (
              <div className="scanner-section-multi">
                <div className="upload-instructions card">
                  <h3>📸 {language === 'hi' ? 'तीन चित्र अपलोड करें' : 'Upload 3 Images'}</h3>
                  <p>{language === 'hi' 
                    ? 'अपनी फसल की विभिन्न कोणों से 3 तस्वीरें लें - ऊपर से, पास से और पूरे पौधे की' 
                    : 'Take 3 pictures of your crop from different angles - top view, close-up, and full plant'}</p>
                  <ul className="angle-tips">
                    <li>📷 {language === 'hi' ? 'चित्र 1: ऊपर से (पत्तियों का शीर्ष दृश्य)' : 'Image 1: Top view (leaves from above)'}</li>
                    <li>🔍 {language === 'hi' ? 'चित्र 2: करीब से (प्रभावित क्षेत्र)' : 'Image 2: Close-up (affected area)'}</li>
                    <li>🌿 {language === 'hi' ? 'चित्र 3: पूर्ण दृश्य (पूरा पौधा)' : 'Image 3: Full view (entire plant)'}</li>
                  </ul>
                </div>

                <div className="multi-upload-grid">
                  {[0, 1, 2].map((index) => (
                    <div key={index} className="upload-slot card">
                      <div className="slot-header">
                        <span className="slot-number">{index + 1}</span>
                        <span className="slot-label">
                          {index === 0 ? (language === 'hi' ? 'ऊपर से' : 'Top View') : 
                           index === 1 ? (language === 'hi' ? 'करीब से' : 'Close-up') : 
                           (language === 'hi' ? 'पूर्ण दृश्य' : 'Full View')}
                        </span>
                      </div>
                      
                      <div className="upload-area-small">
                        {previewUrls[index] ? (
                          <div className="image-preview-small">
                            <img src={previewUrls[index]} alt={`Preview ${index + 1}`} />
                          </div>
                        ) : (
                          <div className="upload-placeholder-small">
                            <Upload size={32} />
                            <p>{language === 'hi' ? 'अपलोड करें' : 'Upload'}</p>
                          </div>
                        )}
                      </div>
                      
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileChange(e, index)}
                        id={`file-input-${index}`}
                        style={{ display: 'none' }}
                      />
                      <label htmlFor={`file-input-${index}`} className="btn-secondary-small">
                        {previewUrls[index] ? (language === 'hi' ? 'बदलें' : 'Change') : (language === 'hi' ? 'चुनें' : 'Select')}
                      </label>
                    </div>
                  ))}
                </div>

                {error && <div className="error-message" data-testid="error-message">{error}</div>}

                <button
                  className="btn-primary start-diagnosis-btn"
                  onClick={handleStartDiagnosis}
                  disabled={!selectedFiles[0] || !selectedFiles[1] || !selectedFiles[2] || scanning}
                  data-testid="scan-btn"
                >
                  {scanning ? (language === 'hi' ? 'विश्लेषण हो रहा है...' : 'Analyzing...') : (language === 'hi' ? 'निदान शुरू करें' : 'Start Diagnosis')}
                </button>
              </div>
            )}

            {/* Image Descriptions Mode - NEW */}
            {diagnosticMode === 'descriptions' && (
              <div className="descriptions-view card">
                <div className="diagnostic-header">
                  <h3>👁️ {language === 'hi' ? 'AI द्वारा देखा गया' : 'What AI Sees'}</h3>
                  <p className="hindi-text">{language === 'hi' ? 'आपकी तस्वीरों में क्या दिख रहा है' : 'Analysis of your images'}</p>
                </div>

                <div className="descriptions-grid">
                  {imageDescriptions.map((desc, index) => (
                    <div key={index} className="description-card">
                      <div className="desc-image">
                        <img src={previewUrls[index]} alt={`Image ${index + 1}`} />
                        <span className="image-badge">{language === 'hi' ? 'चित्र' : 'Image'} {index + 1}</span>
                      </div>
                      <div className="desc-content">
                        <h4>{language === 'hi' ? 'दिख रहा है:' : 'Visible:'}</h4>
                        <p>{language === 'hi' ? desc.description_hi : desc.description_en}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  className="btn-primary"
                  onClick={() => setDiagnosticMode('questions')}
                >
                  {language === 'hi' ? 'आगे बढ़ें - सवाल जवाब' : 'Continue to Questions'}
                </button>
              </div>
            )}

            {/* Questions Mode */}
            {diagnosticMode === 'questions' && questions.length > 0 && (
              <div className="diagnostic-flow card">
                <div className="diagnostic-header">
                  <h3>🩺 AI Doctor Consultation</h3>
                  <p className="hindi-text">AI डॉक्टर परामर्श</p>
                </div>

                {initialObservation && (
                  <div className="observation-box">
                    <h4>Initial Observation / प्रारंभिक अवलोकन:</h4>
                    <p>{language === 'en' ? initialObservation.en : initialObservation.hi}</p>
                  </div>
                )}

                <div className="progress-bar">
                  <div className="progress-fill" style={{width: `${((currentQuestionIndex + 1) / questions.length) * 100}%`}}></div>
                </div>
                <p className="progress-text">Question {currentQuestionIndex + 1} of {questions.length}</p>

                <div className="question-card">
                  <h4>{language === 'en' ? questions[currentQuestionIndex].question_en : questions[currentQuestionIndex].question_hi}</h4>
                  
                  <div className="answer-options">
                    {questions[currentQuestionIndex].options && questions[currentQuestionIndex].options.map((option, idx) => (
                      <button
                        key={idx}
                        className="option-btn"
                        onClick={() => handleAnswerQuestion(option)}
                        disabled={scanning}
                      >
                        {option}
                      </button>
                    ))}
                  </div>

                  {scanning && (
                    <div className="analyzing-box">
                      <div className="loader"></div>
                      <p>Analyzing your response...</p>
                    </div>
                  )}
                </div>

                {conversationHistory.length > 0 && (
                  <div className="conversation-summary">
                    <h4>Your Answers:</h4>
                    {conversationHistory.map((item, idx) => (
                      <div key={idx} className="answer-summary">
                        <strong>Q:</strong> {item.question}<br/>
                        <strong>A:</strong> {item.answer}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Results Mode */}
            {diagnosticMode === 'result' && result && (
              <div className="results-section card" data-testid="results-section">
                <h3>✅ {t.results}</h3>
                
                {/* Most Likely Disease - Primary Diagnosis */}
                <div className="primary-diagnosis">
                  <div className="diagnosis-header">
                    <h4>🎯 {language === 'hi' ? 'सबसे संभावित रोग' : 'Most Likely Disease'}</h4>
                    <span className="probability-badge primary">{result.primary_probability}%</span>
                  </div>
                  <p className="disease-name-large">
                    {language === 'en' ? result.disease_name_en : result.disease_name_hi}
                  </p>
                  <div className={`severity-badge ${result.severity}`}>
                    {t.severity}: {result.severity}
                  </div>
                </div>

                {/* Other Possible Causes */}
                {result.other_causes && result.other_causes.length > 0 && (
                  <div className="other-causes-section">
                    <h4>🔍 {language === 'hi' ? 'अन्य संभावित कारण' : 'Other Possible Causes'}</h4>
                    <div className="causes-list">
                      {result.other_causes.map((cause, index) => (
                        <div key={index} className="cause-item">
                          <div className="cause-header">
                            <span className="cause-name">
                              {language === 'en' ? cause.disease_en : cause.disease_hi}
                            </span>
                            <span className="probability-badge secondary">{cause.probability}%</span>
                          </div>
                          <p className="cause-note">{language === 'en' ? cause.note_en : cause.note_hi}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="result-item">
                  <h4>{t.description}</h4>
                  <p>{language === 'en' ? result.description_en : result.description_hi}</p>
                </div>

                <div className="result-item">
                  <h4>{t.treatment}</h4>
                  <p>{language === 'en' ? result.treatment_en : result.treatment_hi}</p>
                </div>

                {result.prevention_en && user?.tier === 'paid' && (
                  <div className="result-item">
                    <h4>{t.prevention}</h4>
                    <p>{language === 'en' ? result.prevention_en : result.prevention_hi}</p>
                  </div>
                )}

                <button className="btn-secondary" onClick={handleRestart}>
                  Start New Diagnosis / नया निदान शुरू करें
                </button>
              </div>
            )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
